// src/components/WidgetDataModal.tsx
import { useState, useEffect } from 'react';
import {
	Modal,
	Stack,
	Button,
	Checkbox,
	Collapse,
	Text,
	Box,
	Group,
	Badge,
	Alert,
} from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import { useWidgetPreferences } from '../hooks/useWidgetPreferences';
import { getEnabledWidgetsWithBillIntegration } from '../widgets/registry';
import type { BillWidgetData } from '../types/bill';

interface WidgetDataModalProps {
	opened: boolean;
	onClose: () => void;
	existingWidgetData?: BillWidgetData;
	onSave: (widgetData: BillWidgetData) => void;
	isNewBill: boolean;
	billName?: string;
}

function WidgetDataModal({
	opened,
	onClose,
	existingWidgetData = {},
	onSave,
	isNewBill,
	billName,
}: WidgetDataModalProps) {
	const { enabledWidgets } = useWidgetPreferences();
	const widgetsWithIntegrations =
		getEnabledWidgetsWithBillIntegration(enabledWidgets);

	// Track enabled state and data for each widget
	const [widgetStates, setWidgetStates] = useState<BillWidgetData>(() => {
		return existingWidgetData;
	});
	const [validationError, setValidationError] = useState<string | null>(null);

	// Reset state when modal opens with new data
	useEffect(() => {
		if (opened) {
			setWidgetStates(existingWidgetData);
			setValidationError(null);
		}
	}, [opened, existingWidgetData]);

	const handleSave = () => {
		// Validate all enabled widgets
		for (const widget of widgetsWithIntegrations) {
			const widgetData = widgetStates[widget.id];
			if (widgetData && widget.billIntegration?.validateData) {
				const error = widget.billIntegration.validateData(widgetData);
				if (error) {
					setValidationError(error);
					return;
				}
			}
		}

		setValidationError(null);
		onSave(widgetStates);
		onClose();
	};

	const handleCancel = () => {
		setWidgetStates(existingWidgetData);
		onClose();
	};

	// Count how many widgets are configured
	const configuredCount = Object.keys(widgetStates).length;

	return (
		<Modal
			opened={opened}
			onClose={handleCancel}
			title={
				<Group gap="xs">
					<Text fw={500}>Manage Widgets</Text>
					{configuredCount > 0 && (
						<Badge size="sm" variant="light">
							{configuredCount} active
						</Badge>
					)}
				</Group>
			}
			centered
			size="md"
		>
			<Stack gap="md">
				{widgetsWithIntegrations.length === 0 ? (
					<Text size="sm" c="dimmed" ta="center" py="md">
						No widgets with bill integration are enabled.
						Enable widgets on the For You page to see options here.
					</Text>
				) : (
					<>
						<Text size="sm" c="dimmed">
							Connect this bill to your enabled widgets for tracking and insights.
						</Text>

						{widgetsWithIntegrations.map((widget) => {
							const integration = widget.billIntegration!;
							const isEnabled = widgetStates[widget.id] !== undefined;
							const widgetData = widgetStates[widget.id];

							const handleToggle = (checked: boolean) => {
								if (checked) {
									setWidgetStates((prev) => ({
										...prev,
										[widget.id]: integration.defaultData || {},
									}));
								} else {
									setWidgetStates((prev) => {
										const next = { ...prev };
										delete next[widget.id];
										return next;
									});
								}
							};

							const handleDataChange = (
								data: Record<string, unknown> | null
							) => {
								if (data === null) {
									setWidgetStates((prev) => {
										const next = { ...prev };
										delete next[widget.id];
										return next;
									});
								} else {
									setWidgetStates((prev) => ({
										...prev,
										[widget.id]: data,
									}));
								}
							};

							return (
								<Box
									key={widget.id}
									p="sm"
									style={{
										borderRadius: 'var(--mantine-radius-md)',
										border: '1px solid var(--mantine-color-default-border)',
										backgroundColor: isEnabled
											? 'var(--mantine-color-blue-light)'
											: undefined,
									}}
								>
									<Checkbox
										label={integration.formLabel}
										checked={isEnabled}
										onChange={(e) =>
											handleToggle(e.currentTarget.checked)
										}
									/>
									{integration.formDescription && (
										<Text size="xs" c="dimmed" mt={4} ml={30}>
											{integration.formDescription}
										</Text>
									)}
									<Collapse in={isEnabled}>
										<Box mt="sm" ml={30}>
											<integration.FormSection
												widgetData={widgetData}
												onWidgetDataChange={handleDataChange}
												isNewBill={isNewBill}
												billName={billName}
											/>
										</Box>
									</Collapse>
								</Box>
							);
						})}
					</>
				)}

				{validationError && (
					<Alert
						icon={<IconAlertCircle size={16} />}
						color="red"
						variant="light"
					>
						{validationError}
					</Alert>
				)}

				<Group justify="flex-end" mt="md">
					<Button variant="light" onClick={handleCancel}>
						Cancel
					</Button>
					<Button onClick={handleSave}>
						Save
					</Button>
				</Group>
			</Stack>
		</Modal>
	);
}

export default WidgetDataModal;

/**
 * Helper hook to get count of enabled widgets with bill integration
 */
export function useWidgetIntegrationCount(): number {
	const { enabledWidgets } = useWidgetPreferences();
	return getEnabledWidgetsWithBillIntegration(enabledWidgets).length;
}
